import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  TextField,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { Edit, Close, Save, Cancel } from '@mui/icons-material';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';
import AlertMessageDialog from '../../../../components/Dialogs/alertMessageDialog';
import { differenceInDays, parse } from 'date-fns';
import { previewAmortizacion, deriveDiasPagoQuincenal } from '../funciones/preview_amortizacion';

const EditAmortizationDialog = ({ open, onClose, amortizacion, prestamo, moneda, onSave, onRecalcularPlazo }) => {
  const [editedAmortizacion, setEditedAmortizacion] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [openAlert, setOpenAlert] = useState(false);
  const [messageAlert, setMessageAlert] = useState('');
  const [loading, setLoading] = useState(false);
  const [descuentoInteresGlobal, setDescuentoInteresGlobal] = useState('');
  const [nuevoPlazo, setNuevoPlazo] = useState('');
  const [recalculandoPlazo, setRecalculandoPlazo] = useState(false);
  const [previewCuotas, setPreviewCuotas] = useState(null);
  const [previewPlazo, setPreviewPlazo] = useState(null);

  useEffect(() => {
    if (open && amortizacion) {
      setEditedAmortizacion([...amortizacion]);
      setDescuentoInteresGlobal('');
      setNuevoPlazo(prestamo?.plazo != null ? String(prestamo.plazo) : '');
      setPreviewCuotas(null);
      setPreviewPlazo(null);
    }

    if(prestamo.metodo == 'plazo_indefinido') {
      amortizacion.map(cuota => {
        const dateString = prestamo.fecha_inicio;
        const date = new Date(dateString);

        // Get UTC components
        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const year = date.getUTCFullYear();
        const formatted = `${day}-${month}-${year}`;
        var previousAmortizacion = null;                         
        var daysBetween = 0;
                
        previousAmortizacion = cuota.fecha_pagado?cuota.fecha_pagado: formatted;
        if(cuota.dias_acumulados == null || isNaN(cuota.dias_acumulados)) {
          daysBetween = (differenceInDays(new Date(),parse(previousAmortizacion, 'dd-MM-yyyy', new Date())));  
          var interes_nuevo = cuota.capital * ((prestamo.interes/100)/30)*daysBetween;
          var interes_total = interes_nuevo+parseFloat(cuota.interes_acumulado);
          
          cuota.dias_acumulados = (interes_total/(cuota.capital*((prestamo.interes/100)/30))).toFixed(2);
          cuota.interes = interes_total;
          cuota.valor = interes_total;
         }

        if(cuota.descuento == 0) {
          cuota.descuento = '';
        }
        if(cuota.aumento_interes == 0) {
          cuota.aumento_interes = '';
        }

        

      });
    }else{
      amortizacion.map(cuota => {
        if(cuota.descuento == 0) {
          cuota.descuento = '';
        }
        if(cuota.descuento_mora == 0) {
          cuota.descuento_mora = '';
        }
      });
    }
    setEditedAmortizacion(amortizacion);
  }, [open, amortizacion]);

  const parseMoney = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return isNaN(value) ? 0 : value;
    const cleaned = String(value).replaceAll(',', '').replaceAll(' ', '');
    const n = parseFloat(cleaned);
    return isNaN(n) ? 0 : n;
  };

  const normalizeAmortizacionForSave = (rows) => {
    // Backend expects descuento/descuento_mora/aumento_interes as string because it does `.replace(",", "")`
    return (rows || []).map((r) => {
      const descuento = r?.descuento;
      const descuento_mora = r?.descuento_mora;
      const aumento_interes = r?.aumento_interes;

      return {
        ...r,
        descuento: typeof descuento === 'number' ? String(descuento) : descuento,
        descuento_mora: typeof descuento_mora === 'number' ? String(descuento_mora) : descuento_mora,
        aumento_interes: typeof aumento_interes === 'number' ? String(aumento_interes) : aumento_interes,
      };
    });
  };

  const applyGlobalInterestDiscount = async () => {
    const monto = parseMoney(descuentoInteresGlobal);
    if (!monto || monto <= 0) {
      setMessageAlert('Ingresa un monto de descuento válido.');
      setOpenAlert(true);
      return;
    }

    // If user is editing a row, stop editing to avoid confusion
    if (editingRow !== null) setEditingRow(null);

    const updated = editedAmortizacion.map((c) => ({ ...c }));

    let restante = monto;
    let aplicado = 0;

    // Distribute over non-paid installments, in displayed order
    for (let i = 0; i < updated.length && restante > 0; i++) {
      const cuota = updated[i];
      if (cuota?.estado === 'pagado') continue;

      const interes = parseMoney(cuota.interes ?? cuota.valor ?? 0);
      const descuentoActual = parseMoney(cuota.descuento);
      const disponible = Math.max(0, interes - descuentoActual);
      if (disponible <= 0) continue;

      const aAplicar = Math.min(disponible, restante);
      const nuevoDescuento = descuentoActual + aAplicar;

      // Keep as string for backend compatibility
      cuota.descuento = nuevoDescuento === 0 ? '' : String(nuevoDescuento);
      cuota.descuento_interes = nuevoDescuento;

      const capital = parseMoney(cuota.capital);
      if (prestamo.metodo == 'plazo_indefinido') {
        // En plazo indefinido la cuota es solo el interés (menos descuento)
        cuota.cuota = (interes || 0) - (nuevoDescuento || 0);
      } else {
        cuota.cuota = (capital || 0) + (interes || 0) - (nuevoDescuento || 0);
      }

      restante -= aAplicar;
      aplicado += aAplicar;
    }

    setEditedAmortizacion(updated);

    setLoading(true);
    try {
      await onSave(normalizeAmortizacionForSave(updated));
      if (restante > 0) {
        setMessageAlert(
          `Se aplicó un descuento de ${moneda?.label || '$'}${formatNumberWithCommas(aplicado)}. ` +
          `No se pudo aplicar ${moneda?.label || '$'}${formatNumberWithCommas(restante)} porque supera el interés disponible en las cuotas pendientes.`
        );
      } else {
        setMessageAlert(
          `Se aplicó un descuento de ${moneda?.label || '$'}${formatNumberWithCommas(aplicado)} distribuido entre las cuotas.`
        );
      }
      setOpenAlert(true);
    } catch (error) {
      alert(error);
      setMessageAlert('Error al guardar el descuento global de intereses.');
      setOpenAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (index) => {
    // Don't allow editing if the installment is paid
    if (editedAmortizacion[index]?.estado === 'pagado') {
      setMessageAlert('No se puede editar una cuota que ya está pagada.');
      setOpenAlert(true);
      return;
    }
    setEditingRow(index);
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditedAmortizacion([...amortizacion]);
  };

  const handleSaveEdit = async () => {
    setLoading(true);
    try {
      await onSave(normalizeAmortizacionForSave(editedAmortizacion));
      setMessageAlert('Amortización actualizada exitosamente.');
      setOpenAlert(true);
      setEditingRow(null);
    } catch (error) {
      setMessageAlert(error?.message || 'Error al actualizar la amortización.');
      setOpenAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (index, field, value) => {
    const updated = [...editedAmortizacion];
    
    // Don't allow editing date or capital
    if (field === 'fecha' || field === 'capital') {
      return;
    }
    
    
    // Auto-calculate cuota when interes changes

    if (field === 'descuento') {
      if(parseFloat(value) > parseFloat(updated[index].interes)) {
        setMessageAlert('El descuento no puede ser mayor al interes.');
        setOpenAlert(true);
        return;
      }
      updated[index] = { ...updated[index], [field]: value };

      const capital = updated[index].capital;
      const interes = updated[index].interes;
      const descuento = updated[index].descuento;
      if(prestamo.metodo == 'plazo_indefinido') {
        updated[index].descuento_interes = parseFloat(descuento.replaceAll(",",""));
        updated[index].cuota =  (parseFloat(interes) || 0) - (parseFloat(descuento) || 0);
      }else{
        updated[index].descuento_interes = parseFloat(descuento.replaceAll(",",""));
        updated[index].cuota = (parseFloat(capital) || 0) + (parseFloat(interes) || 0) - (parseFloat(descuento) || 0);
      }
    }
    else if (field === 'descuento_mora') {
      if(value > updated[index].mora) {
        setMessageAlert('El descuento de mora no puede ser mayor a la mora.');
        setOpenAlert(true);
        return;
      }
      updated[index] = { ...updated[index], [field]: value };
    }
    else{
      updated[index] = { ...updated[index], [field]: value };

    }
    
    setEditedAmortizacion(updated);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pagado': return 'success';
      case 'pendiente': return 'warning';
      case 'cancelado': return 'error';
      default: return 'default';
    }
  };

  const tienePagosRegistrados = (editedAmortizacion || []).some((c) => {
    if (!c) return false;
    if (c.estado === 'pagado') return true;
    if (parseMoney(c.capitalPagado) > 0) return true;
    if (parseMoney(c.interesPagado) > 0) return true;
    if (parseMoney(c.moraPagado) > 0) return true;
    if (parseMoney(c.valorPagado) > 0) return true;
    return false;
  });

  const handleGenerarPreview = () => {
    const plazoNum = parseInt(String(nuevoPlazo).trim(), 10);
    if (!Number.isFinite(plazoNum) || plazoNum <= 0) {
      setMessageAlert('Ingresa un plazo válido (entero mayor a 0).');
      setOpenAlert(true);
      return;
    }

    if (parseInt(prestamo?.plazo, 10) === plazoNum) {
      setMessageAlert('El nuevo plazo es igual al plazo actual.');
      setOpenAlert(true);
      return;
    }

    if (!Array.isArray(amortizacion) || amortizacion.length === 0 || !amortizacion[0]?.fecha) {
      setMessageAlert('No se pudo determinar la fecha de inicio de las cuotas.');
      setOpenAlert(true);
      return;
    }

    let firstCuotaDate = null;
    try {
      firstCuotaDate = parse(amortizacion[0].fecha, 'dd-MM-yyyy', new Date());
    } catch (_e) {
      firstCuotaDate = null;
    }
    if (!firstCuotaDate || isNaN(firstCuotaDate.getTime())) {
      setMessageAlert('No se pudo interpretar la fecha de la primera cuota.');
      setOpenAlert(true);
      return;
    }

    const diasPagoQuincenal =
      String(prestamo?.frecuencia || '').toLowerCase() === 'quincenal'
        ? deriveDiasPagoQuincenal(amortizacion)
        : null;

    const cuotas = previewAmortizacion({
      metodo: prestamo?.metodo,
      montoSolicitado: prestamo?.monto_solicitado,
      interes: prestamo?.interes,
      plazo: plazoNum,
      frecuencia: prestamo?.frecuencia,
      firstCuotaDate,
      diasPagoQuincenal,
    });

    if (!cuotas || cuotas.length === 0) {
      setMessageAlert('No se pudo generar la vista previa con los datos del préstamo.');
      setOpenAlert(true);
      return;
    }

    setPreviewCuotas(cuotas);
    setPreviewPlazo(plazoNum);
  };

  const handleDescartarPreview = () => {
    setPreviewCuotas(null);
    setPreviewPlazo(null);
  };

  const handleConfirmarRecalculo = async () => {
    if (typeof onRecalcularPlazo !== 'function') {
      setMessageAlert('No se puede recalcular el plazo en este contexto.');
      setOpenAlert(true);
      return;
    }
    if (!Number.isFinite(previewPlazo) || previewPlazo <= 0) {
      setMessageAlert('No hay un plazo válido para confirmar.');
      setOpenAlert(true);
      return;
    }

    setRecalculandoPlazo(true);
    try {
      await onRecalcularPlazo(previewPlazo);
      setMessageAlert('Plazo y tabla de amortización recalculados exitosamente.');
      setOpenAlert(true);
      setPreviewCuotas(null);
      setPreviewPlazo(null);
      onClose();
    } catch (error) {
      setMessageAlert(error?.message || 'Error al recalcular el plazo.');
      setOpenAlert(true);
    } finally {
      setRecalculandoPlazo(false);
    }
  };


  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #55b4ac 0%, #4a9b94 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '50%',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Edit sx={{ fontSize: 24 }} />
            </Box>
            <Typography variant="h6" fontWeight="bold">
              Editar Tabla de Amortización
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box mb={3} mt={2}>
            <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
              Préstamo: {prestamo.loan_id || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cliente: {prestamo.cliente_nombre || 'N/A'}
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Descuento global de intereses
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <TextField
                label="Monto a descontar"
                size="small"
                value={formatNumberWithCommas(descuentoInteresGlobal)}
                onChange={(e) => setDescuentoInteresGlobal(e.target.value)}
                sx={{ width: 220 }}
                placeholder="0.00"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">{moneda?.label || '$'}</InputAdornment>
                    ),
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={applyGlobalInterestDiscount}
                disabled={loading || !editedAmortizacion?.length}
                sx={{ fontWeight: 'bold' }}
              >
                Aplicar y distribuir
              </Button>
              <Typography variant="body2" color="text.secondary">
                Se reparte en orden entre cuotas <strong>pendientes</strong> hasta agotar el monto.
              </Typography>
            </Box>
          </Paper>

          {prestamo.metodo !== 'plazo_indefinido' && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Recalcular plazo
              </Typography>
              {tienePagosRegistrados ? (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  No se puede recalcular el plazo porque el préstamo ya tiene pagos registrados.
                </Alert>
              ) : previewCuotas ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Vista previa de la nueva tabla. Aún no se ha guardado. Los valores definitivos pueden variar por redondeo al guardar.
                </Alert>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Cambia el número de cuotas y se mostrará una vista previa antes de guardar.
                </Typography>
              )}
              <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <TextField
                  label="Nuevo plazo (cuotas)"
                  size="small"
                  type="number"
                  value={nuevoPlazo}
                  onChange={(e) => setNuevoPlazo(e.target.value)}
                  disabled={tienePagosRegistrados || recalculandoPlazo || !!previewCuotas}
                  sx={{ width: 220 }}
                  inputProps={{ min: 1, step: 1 }}
                />
                {!previewCuotas && (
                  <Button
                    variant="contained"
                    onClick={handleGenerarPreview}
                    disabled={tienePagosRegistrados || recalculandoPlazo || !nuevoPlazo}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Vista previa
                  </Button>
                )}
                {previewCuotas && (
                  <>
                    <Button
                      variant="contained"
                      onClick={handleConfirmarRecalculo}
                      disabled={recalculandoPlazo}
                      sx={{
                        fontWeight: 'bold',
                        color: '#fff',
                        backgroundColor: 'green',
                      }}
                      color="primarybutton"
                      startIcon={recalculandoPlazo ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      Confirmar y guardar
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleDescartarPreview}
                      disabled={recalculandoPlazo}
                      sx={{ fontWeight: 'bold' }}
                    >
                      Descartar
                    </Button>
                  </>
                )}
                {prestamo?.plazo != null && (
                  <Typography variant="body2" color="text.secondary">
                    Plazo actual: <strong>{prestamo.plazo}</strong>
                    {previewPlazo != null && (
                      <>
                        {' '}— Nuevo: <strong>{previewPlazo}</strong>
                      </>
                    )}
                  </Typography>
                )}
              </Box>

              {previewCuotas && (
                <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#55b4ac' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                          # Cuota
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                          Fecha
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                          Capital
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                          Interés
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                          Cuota
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewCuotas.map((c) => (
                        <TableRow key={c.numero_cuota} hover>
                          <TableCell sx={{ textAlign: 'center' }}>{c.numero_cuota}</TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>{c.fecha}</TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {`${moneda?.label || '$'}${formatNumberWithCommas(c.capital)}`}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {`${moneda?.label || '$'}${formatNumberWithCommas(c.interes)}`}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {`${moneda?.label || '$'}${formatNumberWithCommas(c.cuota)}`}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          )}

          {!previewCuotas && (prestamo.metodo == 'plazo_indefinido' ? (
                  <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#55b4ac' }}>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                              Interes
                            </TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                              Capital
                            </TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                              Desc. Interes
                            </TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                              Aum. Interes
                            </TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                              Valor Pagado
                            </TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                              Acciones
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {editedAmortizacion.map((amort, index) => (
                            <TableRow key={amort.numero_cuota} hover>
          
                              <TableCell sx={{ textAlign: 'center' }}>
                                {moneda?.label || '$'}${formatNumberWithCommas(amort.valor)}
                              </TableCell>
          
                              <TableCell sx={{ textAlign: 'center' }}>
                                {`${moneda?.label || '$'}${formatNumberWithCommas(amort.capital)}`}
                              </TableCell>
          
          
                              <TableCell sx={{ textAlign: 'center' }}>
                                {editingRow === index ? (
                                  
                                  <TextField
                                    slotProps={{
                                      input: {
                                        startAdornment: (
                                          <InputAdornment position="start">{moneda?.label || '$'}</InputAdornment>
                                        ),
                                      },
                                    }}
                                    size="small"
                                    sx={{ width: 120 }}
                                    value={formatNumberWithCommas(amort.descuento)}
                                    placeholder="0.00"
                                    onChange={(e) => {
                                      handleFieldChange(index, 'descuento', e.target.value)
                                    }}
                                  />
                                ) : (
                                  `${moneda?.label || '$'}${formatNumberWithCommas(amort.descuento)}`
                                )}
                              </TableCell>
                            
                              <TableCell sx={{ textAlign: 'center' }}>
                                {editingRow === index ? (
                                  
                                  <TextField
                                    slotProps={{
                                      input: {
                                        startAdornment: (
                                          <InputAdornment position="start">{moneda?.label || '$'}</InputAdornment>
                                        ),
                                      },
                                    }}
                                    size="small"
                                    sx={{ width: 120 }}
                                    value={formatNumberWithCommas(amort.aumento_interes)}
                                    onChange={(e) => {
                                      handleFieldChange(index, 'aumento_interes', e.target.value)
                                    }}
                                    placeholder="0.00"
                                  />
                                ) : (
                                  `${moneda?.label || '$'}${formatNumberWithCommas(amort.aumento_interes)}`
                                )}
                              </TableCell>
                              
                              <TableCell sx={{ textAlign: 'center' }}>
                              {`${moneda?.label || '$'}${formatNumberWithCommas(amort.valorPagado)}`}
                              </TableCell>
                              
                              
                              <TableCell sx={{ textAlign: 'center' }}>
                                {editingRow === index ? (
                                  <Box display="flex" gap={1} justifyContent="center">
                                    <IconButton
                                      size="small"
                                      onClick={handleSaveEdit}
                                      disabled={loading}
                                      sx={{ color: 'success.main' }}
                                    >
                                      <Save />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={handleCancelEdit}
                                      sx={{ color: 'error.main' }}
                                    >
                                      <Cancel />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Box display="flex" alignItems="center" gap={1} justifyContent="center">
                                 
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEdit(index)}
                                      disabled={amort.estado === 'pagado'}
                                      sx={{ 
                                        color: amort.estado === 'pagado' ? 'grey.400' : 'primary.main',
                                        opacity: amort.estado === 'pagado' ? 0.5 : 1
                                      }}
                                    >
                                      <Edit />
                                    </IconButton>
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
          
          ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#55b4ac' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                    # de Cuota
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                    Fecha
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                    Interes
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                    Capital
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                    Mora
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                    Desc. Interes
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                    Desc. Mora
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                    Cuota
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                    Valor Pagado
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editedAmortizacion.map((amort, index) => (
                  <TableRow key={amort.numero_cuota} hover>
                    <TableCell sx={{ textAlign: 'center' }}>{amort.numero_cuota}</TableCell>
                    
                     <TableCell sx={{ textAlign: 'center' }}>
                       {amort.fecha}
                     </TableCell>

                    <TableCell sx={{ textAlign: 'center' }}>
                      {moneda?.label || '$'}${formatNumberWithCommas(amort.interes)}
                    </TableCell>

                    <TableCell sx={{ textAlign: 'center' }}>
                      {`${moneda?.label || '$'}${formatNumberWithCommas(amort.capital)}`}
                    </TableCell>

                    <TableCell sx={{ textAlign: 'center' }}>
                      {`${moneda?.label || '$'}${formatNumberWithCommas(amort.mora)}`}
                    </TableCell>

                    <TableCell sx={{ textAlign: 'center' }}>
                      {editingRow === index ? (
                        
                        <TextField
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">{moneda?.label || '$'}</InputAdornment>
                              ),
                            },
                          }}
                          size="small"
                          sx={{ width: 120 }}
                          value={formatNumberWithCommas(amort.descuento)}
                          onChange={(e) => {
                            handleFieldChange(index, 'descuento', e.target.value)
                          }}
                        />
                      ) : (
                        `${moneda?.label || '$'}${formatNumberWithCommas(amort.descuento)}`
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {editingRow === index ? (
                       
                        <TextField
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">{moneda?.label}$</InputAdornment>
                                ),
                            },
                        }}
                        size="small"
                        sx={{ width: 120 }}
                        value={formatNumberWithCommas(amort.descuento_mora)}
                        onChange={(e) => {
                            handleFieldChange(index, 'descuento_mora', e.target.value)
                        }}/>
                      ) : (
                        `${moneda?.label || '$'}${formatNumberWithCommas(amort.descuento_mora)}`
                      )}
                    </TableCell>

                    
                     <TableCell sx={{ textAlign: 'center' }}>
                       <TextField
                         type="number"
                         value={amort.cuota}
                         size="small"
                         InputProps={{
                           startAdornment: `${moneda?.label || '$'}`,
                           readOnly: true
                         }}
                         sx={{ 
                           width: 120,
                           '& .MuiInputBase-input': {
                             backgroundColor: '#f5f5f5',
                             color: '#666'
                           }
                         }}
                       />
                     </TableCell>
                    
                    <TableCell sx={{ textAlign: 'center' }}>
                    {`${moneda?.label || '$'}${formatNumberWithCommas(amort.valorPagado)}`}
                    </TableCell>
                    
                    
                    <TableCell sx={{ textAlign: 'center' }}>
                      {editingRow === index ? (
                        <Box display="flex" gap={1} justifyContent="center">
                          <IconButton
                            size="small"
                            onClick={handleSaveEdit}
                            disabled={loading}
                            sx={{ color: 'success.main' }}
                          >
                            <Save />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={handleCancelEdit}
                            sx={{ color: 'error.main' }}
                          >
                            <Cancel />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
                         
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(index)}
                            disabled={amort.estado === 'pagado'}
                            sx={{ 
                              color: amort.estado === 'pagado' ? 'grey.400' : 'primary.main',
                              opacity: amort.estado === 'pagado' ? 0.5 : 1
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          ))}
          {!previewCuotas && editingRow !== null && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Estás editando la cuota {editedAmortizacion[editingRow]?.numero_cuota}. 
              Haz clic en Guardar para confirmar los cambios o Cancelar para descartarlos.
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button onClick={onClose} variant="outlined" sx={{ fontWeight: 'bold' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <AlertMessageDialog 
        message={messageAlert} 
        title="" 
        severity="success" 
        color="success" 
        open={openAlert} 
        handleClose={() => setOpenAlert(false)}
      />
    </>
  );
};

export default EditAmortizationDialog;
